import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Constants } from "@eagerdog/constants";
import { Config } from "@eagerdog/config";

import { apiService } from "src/services";
import { helperService } from "src/services/helper.service";
import { localStorageService } from "src/services/local-storage.service";
import { webSocketService } from "src/services/websocket.service";
import LoadingSpinner from "src/components/LoadingSpinner";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./SubscriptionCheckoutReturn.module.scss";

const SubscriptionCheckoutReturn: React.FC = () => {
  const user = helperService.getUser();
  const club = helperService.getClub();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [waitingForWebhook, setWaitingForWebhook] = useState(false);
  const webhookTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const refreshUserData = async () => {
    try {
      const updatedUser = await apiService.whoAmI();
      localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
      
      // Trigger custom event for other components to listen to
      window.dispatchEvent(new Event('userDataUpdated'));
      
      return updatedUser;
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      return null;
    }
  };

  const refreshClubData = async () => {
    try {
      // For club managers and users with admin capacity, fetch current club
      const updatedClub = await apiService.getCurrentClub();
      localStorageService.setItem(localStorageService.CLUB, JSON.stringify(updatedClub));
      return updatedClub;
    } catch (error) {
      console.error("Failed to refresh club data:", error);
      return null;
    }
  };

  const handleSuccessfulSubscription = async () => {
    const isClubManagerOrAdmin = user.user_type === Constants.user_type.club_manager || 
      (club._id && helperService.hasAdmin(club._id));

    // Refresh user data
    await refreshUserData();
    
    // For club managers and admins, also refresh club data
    let redirectClubId = club._id;
    if (isClubManagerOrAdmin) {
      const updatedClub = await refreshClubData();
      if (updatedClub) {
        redirectClubId = updatedClub._id;
      }
    }
    
    const link = user.user_type === Constants.user_type.owner 
      ? "/account?t=6" 
      : `/k/${redirectClubId}/manage/billing`;
    
    toast.show({
      title: "Subscription",
      content: "Your subscription has been created successfully!",
      duration: 5000,
      type: "success"
    });
    
    // Force reload to ensure all components (Header, etc.) pick up fresh data
    window.location.href = link;
  };

  // WebSocket connection for real-time subscription updates
  useEffect(() => {
    const token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
    if (!token) return;

    // Connect to subscription WebSocket
    webSocketService.connect({
      url: Config.web.REACT_APP_API_URL || 'http://localhost:3001',
      namespace: '/subscriptions',
      token,
    });

    // Listen for subscription updates
    const handleSubscriptionUpdate = (data: any) => {
      if (waitingForWebhook) {
        // Clear fallback timeout
        if (webhookTimeoutRef.current) {
          clearTimeout(webhookTimeoutRef.current);
          webhookTimeoutRef.current = null;
        }
        
        setWaitingForWebhook(false);
        handleSuccessfulSubscription();
      }
    };

    // Subscribe to subscription updates
    const unsubscribeEvent = webSocketService.onCustomEvent('subscription_updated', handleSubscriptionUpdate);

    // Cleanup on unmount
    return () => {
      unsubscribeEvent();
      if (webhookTimeoutRef.current) {
        clearTimeout(webhookTimeoutRef.current);
      }
      webSocketService.disconnect();
    };
  }, [waitingForWebhook]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    apiService.getCheckoutSession(sessionId ?? "")
      .then(async (response) => {
        setStatus(response.status);
        
        if (response.status === "complete") {
          // Set waiting state and start listening for webhook
          setWaitingForWebhook(true);
          
          // Fallback: if webhook doesn't arrive within 10 seconds, proceed anyway
          webhookTimeoutRef.current = setTimeout(() => {
            setWaitingForWebhook(false);
            handleSuccessfulSubscription();
          }, 10000);
        }
      })
      .catch(() => {
        const link = user.user_type === Constants.user_type.owner 
          ? "/account?t=6" 
          : `/k/${club._id}/manage/billing`;
          
        toast.show({
          title: "Subscription",
          content: "There was an issue verifying your subscription. Please check your account settings.",
          duration: 5000,
          type: "fail"
        });
        navigate(link);
      });
  }, [searchParams, navigate]);

  return (
    <div className={styles.container}>
      <title>Processing Subscription...</title>
      {status === "open" && <p>Payment incomplete. Please try again.</p>}
      {!status && <LoadingSpinner />}
    </div>
  )
}

export { SubscriptionCheckoutReturn }
